import type { AnalysisContext, MapData, SourceFile, Transcript } from "../types";

export const SESSION_SCHEMA_VERSION = 2;
export const SESSION_STORAGE_KEY = "sherlock.session";

export interface PersistedSession {
  schemaVersion: number;
  transcripts: Transcript[];
  sources: SourceFile[];
  context: AnalysisContext | null;
  mapData: MapData | null;
}

export const EMPTY_SESSION: PersistedSession = {
  schemaVersion: SESSION_SCHEMA_VERSION,
  transcripts: [],
  sources: [],
  context: null,
  mapData: null,
};

/**
 * Reads the persisted session and discards it if the schema version mismatches.
 * Avoids cryptic crashes when MapData/AnalysisContext shapes evolve.
 */
export function loadSession(): PersistedSession {
  if (typeof window === "undefined") return EMPTY_SESSION;
  try {
    const raw = window.localStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return EMPTY_SESSION;
    const parsed = JSON.parse(raw) as Partial<PersistedSession>;
    if (parsed.schemaVersion !== SESSION_SCHEMA_VERSION) {
      window.localStorage.removeItem(SESSION_STORAGE_KEY);
      return EMPTY_SESSION;
    }
    return {
      schemaVersion: SESSION_SCHEMA_VERSION,
      transcripts: parsed.transcripts ?? [],
      sources: parsed.sources ?? [],
      context: parsed.context ?? null,
      mapData: parsed.mapData ?? null,
    };
  } catch {
    return EMPTY_SESSION;
  }
}

export function saveSession(session: PersistedSession): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      SESSION_STORAGE_KEY,
      JSON.stringify({ ...session, schemaVersion: SESSION_SCHEMA_VERSION }),
    );
  } catch {
    // Quota exceeded — fail silently. We could warn but most sessions stay small.
  }
}

export function clearSession(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    /* noop */
  }
}
